/**
 * Heap Analytics Utility Functions
 * Provides a clean interface for tracking events and managing user identity in Heap
 */

/**
 * Check if Heap is available and loaded
 */
const isHeapAvailable = () => {
  return typeof window !== 'undefined' && !!window.heap;
};

/**
 * Log a warning if Heap is not available (only in development)
 */
const warnIfHeapNotAvailable = action => {
  if (!isHeapAvailable() && process.env.NODE_ENV === 'development') {
    console.warn(
      `[Heap Analytics] Cannot ${action}: Heap is not loaded. Make sure REACT_APP_HEAP_APP_ID is set.`
    );
  }
};

/**
 * Track a custom event in Heap
 * @param {string} eventName - The name of the event to track
 * @param {Object} properties - Optional properties to attach to the event
 *
 * @example
 * trackEvent('task_created', { taskId: '123', taskStatus: 'todo' });
 */
export const trackEvent = (eventName, properties) => {
  if (!isHeapAvailable()) {
    warnIfHeapNotAvailable('track event');
    return;
  }

  try {
    window.heap.track(eventName, properties);
  } catch (error) {
    console.error('[Heap Analytics] Error tracking event:', error);
  }
};

/**
 * Identify a user in Heap
 * This should be called when a user logs in
 * @param {string} userId - Unique identifier for the user
 *
 * @example
 * identifyUser('user_12345');
 */
export const identifyUser = userId => {
  if (!isHeapAvailable()) {
    warnIfHeapNotAvailable('identify user');
    return;
  }

  try {
    window.heap.identify(userId);
  } catch (error) {
    console.error('[Heap Analytics] Error identifying user:', error);
  }
};

/**
 * Reset the user's identity in Heap
 * This should be called when a user logs out
 *
 * @example
 * resetUserIdentity();
 */
export const resetUserIdentity = () => {
  if (!isHeapAvailable()) {
    warnIfHeapNotAvailable('reset user identity');
    return;
  }

  try {
    window.heap.resetIdentity();
  } catch (error) {
    console.error('[Heap Analytics] Error resetting user identity:', error);
  }
};

/**
 * Add properties to the current user
 * These properties will be associated with the user across all events
 * @param {Object} properties - Properties to add to the user
 *
 * @example
 * addUserProperties({
 *   merchantId: 'merchant_123',
 *   hasSKU: true,
 *   hasActiveIntegration: true
 * });
 */
export const addUserProperties = properties => {
  if (!isHeapAvailable()) {
    warnIfHeapNotAvailable('add user properties');
    return;
  }

  try {
    window.heap.addUserProperties(properties);
  } catch (error) {
    console.error('[Heap Analytics] Error adding user properties:', error);
  }
};

/**
 * Add properties that will be sent with all subsequent events
 * @param {Object} properties - Properties to add to all events
 *
 * @example
 * addEventProperties({ channel: 'profit-assist' });
 */
export const addEventProperties = properties => {
  if (!isHeapAvailable()) {
    warnIfHeapNotAvailable('add event properties');
    return;
  }

  try {
    window.heap.addEventProperties(properties);
  } catch (error) {
    console.error('[Heap Analytics] Error adding event properties:', error);
  }
};

/**
 * Remove a specific event property
 * @param property - Name of the property to remove
 */
export const removeEventProperty = property => {
  if (!isHeapAvailable()) {
    warnIfHeapNotAvailable('remove event property');
    return;
  }

  try {
    window.heap.removeEventProperty(property);
  } catch (error) {
    console.error('[Heap Analytics] Error removing event property:', error);
  }
};

/**
 * Clear all event properties
 */
export const clearEventProperties = () => {
  if (!isHeapAvailable()) {
    warnIfHeapNotAvailable('clear event properties');
    return;
  }

  try {
    window.heap.clearEventProperties();
  } catch (error) {
    console.error('[Heap Analytics] Error clearing event properties:', error);
  }
};

/**
 * Get the current session ID
 * @returns {string|null} The current session ID or null if not available
 */
export const getSessionId = () => {
  if (!isHeapAvailable()) {
    return null;
  }

  try {
    return window.heap.getSessionId();
  } catch (error) {
    console.error('[Heap Analytics] Error getting session ID:', error);
    return null;
  }
};

/**
 * Get the current user ID
 * @returns {string|null} The current user ID or null if not available
 */
export const getUserId = () => {
  if (!isHeapAvailable()) {
    return null;
  }

  try {
    return window.heap.getUserId();
  } catch (error) {
    console.error('[Heap Analytics] Error getting user ID:', error);
    return null;
  }
};

/**
 * Track a pageview
 * Note: Heap automatically tracks pageviews, but this can be used for manual tracking
 * @param {Object} properties - Optional properties to attach to the pageview
 */
export const trackPageview = properties => {
  if (!isHeapAvailable()) {
    warnIfHeapNotAvailable('track pageview');
    return;
  }

  try {
    window.heap.trackPageview(properties);
  } catch (error) {
    console.error('[Heap Analytics] Error tracking pageview:', error);
  }
};

/**
 * Track page navigation
 * @param {string} pageName - The name of the page being navigated to
 * @param {string} pagePath - The path/route of the page
 * @param {Object} additionalProperties - Optional additional properties
 */
export const trackPageNavigation = (
  pageName,
  pagePath,
  additionalProperties = {}
) => {
  if (!isHeapAvailable()) {
    warnIfHeapNotAvailable('track page navigation');
    return;
  }

  try {
    const properties = {
      pageName,
      pagePath,
      pageUrl: window.location.href,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      ...additionalProperties,
    };

    window.heap.track('page_navigation', properties);
  } catch (error) {
    console.error('[Heap Analytics] Error tracking page navigation:', error);
  }
};

/**
 * Initialize Heap Analytics
 * This should be called once when the app starts
 * Sets up global event properties like appVersion and channel
 */
export const initializeHeapAnalytics = () => {
  if (!isHeapAvailable()) {
    console.warn(
      '[Heap Analytics] Heap is not available. Make sure REACT_APP_HEAP_APP_ID is set in your .env file.'
    );
    return;
  }

  try {
    // Add global event properties
    addEventProperties({
      appVersion: process.env.REACT_APP_VERSION || 'unknown',
      channel: 'profit-assist',
    });
  } catch (error) {
    console.error('[Heap Analytics] Error initializing:', error);
  }
};
